// CommonExportUtils.js
// Put this in src/utils or wherever you keep shared client utilities.

import * as htmlToImage from 'html-to-image';
import jsPDF from 'jspdf';

const DEFAULT_EXCLUDE = ['.vertical-dimension', '.horizontal-dimension', '.corner', '.zoom-toggle', '.dashed-border', '.preview-header', '.preview-footer'];
const LOG = false; // set true while debugging

function lg(...args) { if (LOG) console.log('[CommonExportUtils]', ...args); }

function reduceInnerWrapperBorderForMobile(root) {
  if (!root) return;

  const el = root.querySelector('.inner-wrapper');
  if (!el) return;

  const parent = el.closest('.color-border, .dark-border');
  if (!parent) return;

  const cs = window.getComputedStyle(el);

  // Save originals
  el.dataset._origBorder = cs.border;
  el.dataset._origBoxShadow = el.style.boxShadow || '';

  // Kill real border
  el.style.border = 'none';

  // ✅ FORCE a visually thinner border using shadow
  // 0.5px is the thinnest reliably renderable line on mobile
  el.style.boxShadow = 'inset 0 0 0 0.5px rgba(255,255,255,0.9)';
}


function restoreInnerWrapperBorder(root) {
  const el = root?.querySelector('.inner-wrapper');
  if (!el || !el.dataset._origBorder) return;

  el.style.border = el.dataset._origBorder;
  el.style.boxShadow = el.dataset._origBoxShadow || '';

  delete el.dataset._origBorder;
  delete el.dataset._origBoxShadow;
}


function reduceStrokesForMobile(
  root,
  {
    largeGroupFactor = 1.5,   // for 1000+ paths
    smallGroupFactor = 2.3,   // for other groups
    largeGroupThreshold = 1000
  } = {}
) {
  if (!root) return;

  const svg = root.querySelector('svg');
  if (!svg) return;

  // 1. Process each <g> independently
  svg.querySelectorAll('g').forEach(g => {
    const paths = g.querySelectorAll('path');
    const pathCount = paths.length;

    const factor =
      pathCount >= largeGroupThreshold
        ? largeGroupFactor
        : smallGroupFactor;

    // store factor for restore/debug
    g.dataset._strokeReduceFactor = factor;

    // handle group stroke-width
    const swAttr = g.getAttribute('stroke-width');
    if (swAttr && !isNaN(parseFloat(swAttr))) {
      g.dataset._origStrokeWidth = swAttr;
      g.setAttribute('stroke-width', parseFloat(swAttr) / factor);
    }

    // handle paths with computed stroke-width (NaN cases)
    paths.forEach(p => {
      const cs = getComputedStyle(p);
      const sw = parseFloat(cs.strokeWidth);
      if (sw > 0) {
        p.dataset._origStrokeWidth = sw;
        p.style.strokeWidth = `${sw / factor}px`;
      }
    });
  });

  // 2. Disable non-scaling-stroke (CRITICAL)
  svg.querySelectorAll('[vector-effect="non-scaling-stroke"]').forEach(el => {
    el.dataset._origVectorEffect = 'non-scaling-stroke';
    el.removeAttribute('vector-effect');
  });
}



function restoreStrokesForMobile(root) {
  if (!root) return;

  const svg = root.querySelector('svg');
  if (!svg) return;

  svg.querySelectorAll('g').forEach(g => {
    if (g.dataset._origStrokeWidth) {
      g.setAttribute('stroke-width', g.dataset._origStrokeWidth);
      delete g.dataset._origStrokeWidth;
    }
    delete g.dataset._strokeReduceFactor;
  });

  svg.querySelectorAll('path[data-_orig-stroke-width]').forEach(p => {
    p.style.strokeWidth = `${p.dataset._origStrokeWidth}px`;
    delete p.dataset._origStrokeWidth;
  });

  svg.querySelectorAll('[data-_orig-vector-effect]').forEach(el => {
    el.setAttribute('vector-effect', 'non-scaling-stroke');
    delete el.dataset._origVectorEffect;
  });
}


/**
 * Build a filter function for html-to-image using excludeSelectors.
 * - Supports class selectors ('.classname') and arbitrary selectors.
 */
export function makeHtmlToImageFilter(excludeSelectors = DEFAULT_EXCLUDE) {
  return function filter(node) {
    if (!(node instanceof Element)) return true;
    const cls = node.classList;
    if (!cls) return true;
    for (const sel of excludeSelectors || []) {
      if (!sel) continue;
      if (sel.startsWith('.')) {
        if (cls.contains(sel.slice(1))) return false;
      } else {
        try {
          if (node.matches(sel)) return false;
        } catch (e) {
          // ignore invalid selector
        }
      }
    }
    return true;
  };
}

/**
 * Capture a thumbnail PNG dataURL from DOM.
 *
 * Options:
 *  - selector: DOM selector for element to capture (default ".frame-container")
 *  - targetWidthPx: final width in px (default 200)
 *  - excludeSelectors: array of selectors to exclude (default DEFAULT_EXCLUDE)
 *  - cacheBust: boolean (default true)
 *  - backgroundColor: optional override (otherwise computed from element)
 */
export async function captureThumbnailPng({
  selector = '.frame-container',
  targetWidthPx = 200,
  excludeSelectors = DEFAULT_EXCLUDE,
  cacheBust = true,
  backgroundColor = null,
} = {}) {
  const node = document.querySelector(selector);
  if (!node) throw new Error(`captureThumbnailPng: selector not found: ${selector}`);

  const rect = node.getBoundingClientRect();
  const cssWidth = Math.max(1, rect.width);
  const pixelRatio = Math.max(1, targetWidthPx / cssWidth);

  const bg = backgroundColor || window.getComputedStyle(node).backgroundColor || '#ffffff';

  lg('captureThumbnailPng', { selector, cssWidth, pixelRatio, targetWidthPx, excludeSelectors });

  const dataUrl = await htmlToImage.toPng(node, {
    pixelRatio,
    backgroundColor: bg,
    cacheBust,
    filter: makeHtmlToImageFilter(excludeSelectors)
  });

  // ensure final width (rare cases canvas toDataURL rounding) - but html-to-image already produced targetWidth
  return dataUrl;
}
/**
 * Generate a PDF (dataurl string) from DOM element using inches for page size.
 *
 * Options:
 *  - selector: '.canvas' by default
 *  - frameSizeInStr: '12x16' (width x height in inches) default '12x16'
 *  - dpi: number, print DPI (default 300)
 *  - excludeSelectors: array (default DEFAULT_EXCLUDE)
 *  - cover: boolean (true to fill/crop, false to contain) default false
 *  - cacheBust: boolean default true
 *
 * Returns: Promise<string> -> dataurl 'data:application/pdf;base64,...'
 */
export async function generatePdfFromDomUsingInches(
  selector = '.canvas',
  frameSizeInStr = '12x16',
  /* dpi param accepted for compatibility but we force 300 DPI for printing */
  dpi = 300,
  excludeSelectors = DEFAULT_EXCLUDE,
  /* cover param accepted but we force cover behavior to fill the page */
  cover = true,
  cacheBust = true,
  backgroundColor = null,
  material = 'paper',
) {
  const node = document.querySelector(selector);
  
  if (!node) throw new Error(`generatePdfFromDomUsingInches: selector not found: ${selector}`);

  // Force print DPI
  const PRINT_DPI = 300;
  dpi = PRINT_DPI;

  // parse inches
  const [wStr, hStr] = String(frameSizeInStr || '12x16').split('x');
  const gelatoFrameExtraMarginIn = material === 'canvas' ? 4 + 0.315 : 0.315; // gelato requires extra bleed margin 

  const wIn = parseFloat(wStr) + gelatoFrameExtraMarginIn || 12.315;
  const hIn = parseFloat(hStr) + gelatoFrameExtraMarginIn || 16.315;
  
  // element CSS px size
  const rect = node.getBoundingClientRect();
  const cssW = Math.max(1, Math.round(rect.width));
  const cssH = Math.max(1, Math.round(rect.height));

  // desired printed area (we want to cover whole page)
  const drawInW = wIn;
  const drawInH = hIn;

  // compute pixel targets for desired print size
  const targetPxW = Math.max(1, Math.round(drawInW * dpi));
  const targetPxH = Math.max(1, Math.round(drawInH * dpi));

  // compute pixelRatio for html-to-image (request at least the needed pixels)
  const pixelRatio = Math.max(targetPxW / cssW, targetPxH / cssH);

  // IMPORTANT: use only the .canvas background (computed style of the node)
  // If node is a wrapper of the canvas, try to find the canvas child to get its background.
  let canvasBg = backgroundColor || window.getComputedStyle(node).backgroundColor || '#ffffff';
  try {
    const canvasChild = node.matches && node.matches('.canvas') ? node : node.querySelector('.canvas');
    if (canvasChild) {
      const cs = window.getComputedStyle(canvasChild);
      if (cs && cs.backgroundColor) canvasBg = cs.backgroundColor;
    }
  } catch (e) {
    // ignore - fallback to computed background of node
  }

  lg('generatePdfFromDomUsingInches (300dpi, cover):', {
    selector, frameSizeInStr, dpi, cssW, cssH, targetPxW, targetPxH, pixelRatio, canvasBg
  });

  // helper: clone a node and inline computed styles recursively
  function cloneAndInlineStyles(srcNode) {
    const clone = srcNode.cloneNode(true);

    const origEls = [srcNode];
    const cloneEls = [clone];

    // BFS through the tree and inline computed styles
    for (let i = 0; i < origEls.length; i++) {
      const orig = origEls[i];
      const c = cloneEls[i];
      try {
        const cs = window.getComputedStyle(orig);
        if (cs) {
          c.style.cssText = cs.cssText || ''; // copy computed cssText (best-effort)
        }
      } catch (e) {
        // ignore computed style read errors
      }

      // push children
      const origChildren = orig.children || [];
      const cloneChildren = c.children || [];
      for (let j = 0; j < origChildren.length; j++) {
        origEls.push(origChildren[j]);
        cloneEls.push(cloneChildren[j]);
      }
    }

    // append offscreen so html-to-image can render it (but keep it invisible)
    clone.style.position = 'fixed';
    clone.style.left = '-9999px';
    clone.style.top = '0px';
    clone.style.zIndex = '-9999';
    document.body.appendChild(clone);
    return clone;
  }

  // Try primary capture. If embed-webfonts reads cross-origin cssRules this may throw.
  let pngDataUrl;
  const isMobile = window.innerWidth <= 768;

  let __prevScrollY = 0;
  let __prevOverflow = '';

  if (isMobile) {
    __prevScrollY = window.scrollY;
    __prevOverflow = document.body.style.overflow;

    window.scrollTo(0, 0);
    document.body.style.overflow = 'hidden';
  }

  try {
    // Hide only dashed borders during PDF export
    //toggleDashedBorderVisibility(true);
    if (isMobile) {
      reduceStrokesForMobile(node, {
        largeGroupFactor: 1.5,   // 1000+ paths group
        smallGroupFactor: 2.3,   // other 2 groups
        largeGroupThreshold: 1000
      });
     //reduceCssForMobile(node, 2);
     reduceInnerWrapperBorderForMobile(node);
    }
    pngDataUrl = await htmlToImage.toPng(node, {
      pixelRatio,
      backgroundColor: canvasBg,
      cacheBust,
      filter: makeHtmlToImageFilter(excludeSelectors)
    });

  } catch (err) {
    const msg = String(err?.message || err);
    console.warn('html-to-image primary capture failed:', msg);

    if (msg.includes('cssRules') || /SecurityError/i.test(msg)) {
      let clone;

      try {
        clone = cloneAndInlineStyles(node);

        // Hide dashed-border on the clone only
        const dashed = clone.querySelectorAll('.dashed-border');
        dashed.forEach(n => { n.style.border = 'none'; });
        if (isMobile) {
          reduceStrokesForMobile(node, {
            largeGroupFactor: 1.5,   // 1000+ paths group
            smallGroupFactor: 2.3,   // other 2 groups
            largeGroupThreshold: 1000
          });
          //reduceCssForMobile(node, 2);
          reduceInnerWrapperBorderForMobile(node);
        }
        pngDataUrl = await htmlToImage.toPng(clone, {
          pixelRatio,
          backgroundColor: canvasBg,
          cacheBust,
          filter: makeHtmlToImageFilter(excludeSelectors)
        });

      } finally {
        if (isMobile) {
          restoreStrokesForMobile(node);
          //restoreCssForMobile(node);
          restoreInnerWrapperBorder(node);
        }
        //toggleDashedBorderVisibility(false);
        try { clone && clone.remove(); } catch {}
      }

    } else {
      throw err;
    }

  } finally {
    // Restore dashed borders on original DOM
    if (isMobile) {
      document.body.style.overflow = __prevOverflow;
      window.scrollTo(0, __prevScrollY);
    }

    //toggleDashedBorderVisibility(false);
  }


  // load produced PNG to inspect pixel size
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.crossOrigin = 'anonymous';
    i.onload = () => resolve(i);
    i.onerror = (e) => reject(new Error('Failed to load generated PNG into Image object: ' + e));
    i.src = pngDataUrl;
  });

  const imgPxW = Math.max(1, img.naturalWidth || img.width);
  const imgPxH = Math.max(1, img.naturalHeight || img.height);

  // convert produced image pixels into inches using forced DPI
  const imgInW = imgPxW / dpi;
  const imgInH = imgPxH / dpi;

  // COVER the PDF page (scale so image fills the page; cropping happens as needed)
  const scale = Math.max(wIn / imgInW, hIn / imgInH);
  const finalInW = imgInW * scale;
  const finalInH = imgInH * scale;

  // center the image on the PDF page
  const offXIn = (wIn - finalInW) / 2;
  const offYIn = (hIn - finalInH) / 2;

  // Create jsPDF in inches and add image at computed physical size
  const pdf = new jsPDF({
    orientation: wIn >= hIn ? 'landscape' : 'portrait',
    unit: 'in',
    format: [wIn, hIn],
    compress: true
  });

  // Add PNG: coordinates and sizes are in inches
  pdf.addImage(pngDataUrl, 'PNG', offXIn, offYIn, finalInW, finalInH);

  // return dataurl string
  return pdf.output('dataurlstring');
}


/**
 * Convert a dataURL (base64) -> Blob for multipart upload
 * Works for application/pdf and image types
 */
export function dataURLToBlob(dataUrl) {
  if (!dataUrl || typeof dataUrl !== 'string') throw new Error('dataURLToBlob: invalid dataUrl');

  const parts = dataUrl.split(',');
  if (parts.length !== 2) {
    // Might be non-standard; try to find the comma
    const idx = dataUrl.indexOf(',');
    if (idx === -1) throw new Error('dataURLToBlob: missing comma separator in dataurl');
    parts[0] = dataUrl.substring(0, idx);
    parts[1] = dataUrl.substring(idx + 1);
  }

  const meta = parts[0];
  const b64 = parts[1];

  const mimeMatch = meta.match(/data:([^;]+);/);
  const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';

  // decode base64
  const binary = atob(b64);
  const len = binary.length;
  const u8 = new Uint8Array(len);
  for (let i = 0; i < len; i++) u8[i] = binary.charCodeAt(i);

  return new Blob([u8], { type: mime });
}

/**
 * Convenience wrapper: create FormData for upload.
 * Params:
 *  - pdfDataUrl (string): 'data:application/pdf;base64,...'
 *  - pdfFileName (string): desired filename (e.g. design_highres.pdf)
 *  - thumbnailDataUrl (string): small PNG base64 (optional)
 *  - otherFields (object): key->value to append to form
 *
 * Returns: FormData instance with highres_file (Blob) and other fields appended.
 */
export function buildUploadFormData({
  pdfDataUrl,
  pdfFileName = 'highres.pdf',
  thumbnailDataUrl = null,
  thumbnailField = 'thumbnail',
  otherFields = {}
} = {}) {
  if (!pdfDataUrl) throw new Error('buildUploadFormData: pdfDataUrl required');

  const fd = new FormData();
  const pdfBlob = dataURLToBlob(pdfDataUrl);
  fd.append('highres_file', pdfBlob, pdfFileName);

  if (thumbnailDataUrl) {
    // older server expects thumbnail as 'thumbnail' base64 field; keep the same key
    fd.append(thumbnailField, thumbnailDataUrl);
  }

  // append other fields (strings or JSON)
  for (const k of Object.keys(otherFields || {})) {
    const v = otherFields[k];
    if (typeof v === 'string') fd.append(k, v);
    else fd.append(k, JSON.stringify(v));
  }

  return fd;
}
